import React, { useState } from 'react';
import FormAndCalloutBox from '../../../3_Organisms/FormAndCalloutBox/FormAndCalloutBox';
import parse from 'html-react-parser';
import Input from '../../../1_Atoms/Forms/Input/Input';
import Button from '../../../1_Atoms/Button/Button';
import FormGroup from '../../../2_Molecules/FormGroup/FormGroup';
import { handleRequest } from '../../../../utils/httpRequest';
import Form from '../../../2_Molecules/Form/Form';
import { languageController } from '../../../../controllers/language/languageController';
import { useForm } from '../../../../hooks/useForm';
import Textarea from '../../../1_Atoms/Forms/Textarea/Textarea';
import Checkbox from '../../../1_Atoms/Forms/Checkbox/Checkbox';
import { autoFormatPhoneNumber, convertLangCodeToSingleLetter, formatDate, pushToGTMDataLayer } from '../../../../utils/helpers';
import CalloutBoxFull from '../../../2_Molecules/CalloutBox/CalloutBoxFull';
import pidController from '../../../../controllers/pidController';
import { getEndpointUrl } from '../../../../utils/endPoints';
import { VehicleData, ServiceDetails, DealerData, DateAndTimeData, ContactDetails } from '../BookServiceAppointment';

interface ContactDetailsFormData {
  firstname: string;
  lastname: string;
  email: string;
  postalcode: string;
  message: string;
  optin: string;
  phone: string;
  [key: string]: string | number | boolean;
}

interface ContactDetailsStepProps {
  handleNextStep: (data: { contactDetails: ContactDetails }) => void;
  dealerData?: DealerData;
  dateAndTimeData?: DateAndTimeData;
  vehicleData?: VehicleData;
  serviceDetails?: ServiceDetails;
  formLanguage: string;
  formDisclaimer: string;
}

interface SubmitPayload {
  firstname: string;
  lastname: string;
  email: string;
  postalcode: string;
  message: string;
  optin: string;
  phone: string;
  language: string;
  pid: string;
  dealerid?: string;
  odometer?: string;
  vehiclemodel?: string;
  vehicleyear?: string;
  vinnumber?: string;
  servicerequired?: string;
  additionalinformation?: string;
  date?: string;
  time?: string;
  [key: string]: any;
}

interface ApiResponse {
  Success: boolean;
  state?: string;
  Errors?: string[];
}

const ContactDetailsStep: React.FC<ContactDetailsStepProps> = ({
  handleNextStep,
  dealerData,
  dateAndTimeData,
  vehicleData,
  serviceDetails,
  formLanguage,
  formDisclaimer
}) => {
  const [formError, setFormError] = useState<string | null>(null);

  const { dealer: selectedDealerData, userPosition } = dealerData || {};

  const initState: ContactDetailsFormData = {
    firstname: '',
    lastname: '',
    email: '',
    postalcode: '',
    message: '',
    optin: 'off', // 'on' or 'off'
    phone: ''
  };

  const validations: { [key: string]: any } = {
    firstname: ['required', 'name', 'nameMaxMin'],
    lastname: ['required', 'name', 'nameMaxMin'],
    email: ['required', 'email'],
    postalcode: ['required', 'postalcode'],
    phone: ['phone']
  };

  const { handleSubmit, handleChange, data: formData, errors, setData } = useForm({
    initState,
    validations,
    // Send the form data if validated
    onSubmit: async () => {
      try {
        const otherData: Partial<SubmitPayload> = {
          language: convertLangCodeToSingleLetter(formLanguage as 'en' | 'fr'),
          pid: String(pidController.getPid('serviceAppt'))
        };

        // Add selectedDealerData data
        if (selectedDealerData) {
          otherData.dealerid = selectedDealerData.dealer_code;
        }
        console.log(vehicleData);
        // Add vehicleData data
        if (vehicleData) {
          otherData.odometer = vehicleData.kilometers?.toString();
          otherData.vehiclemodel = vehicleData.model;
          otherData.vehicleyear = vehicleData.modelYear;
          otherData.vinnumber = vehicleData.vin;
        }

        // Add serviceDetails data
        if (serviceDetails) {
          otherData.servicerequired = serviceDetails.services;
          otherData.additionalinformation = serviceDetails.message;
        }

        // Add Date and Time data
        if (dateAndTimeData) {
          otherData.date = dateAndTimeData.date.toISOString().split('T')[0];
          otherData.time = dateAndTimeData.time;
        }

        // Remove whitespace in postalcode
        const processedFormData = { ...formData };
        if (typeof processedFormData.postalcode === 'string') {
          processedFormData.postalcode = processedFormData.postalcode.replace(/ /g, "");
        }

        const endpoint = getEndpointUrl('service-appt');
        if (!endpoint) throw new Error('Endpoint not found');
        const res = await handleRequest('post', endpoint, { ...processedFormData, ...otherData }) as ApiResponse;

        if (res.Success) {
          pushToGTMDataLayer({ formevent: 'submit' });
          handleNextStep({ 
            contactDetails: {
              firstname: formData.firstname as string,
              lastname: formData.lastname as string,
              email: formData.email as string,
              postalcode: formData.postalcode as string,
              message: formData.message as string,
              optin: formData.optin as string,
              phone: formData.phone as string
            }
          });
        } else {
          setFormError(res.state ? res.state : (res.Errors?.[0] || 'An error occurred'));
        }
      } catch (err) {
        console.log(err);
      }
    },
  });

  const { firstname, lastname, email, postalcode, message, optin, phone } = formData;

  const isFormFilled = (firstname && lastname && email && postalcode);

  return (
    <div className="mz-contact-details-step">
      <div className="mz-contact-details-step__top">
        <div className="mz-container">
          <div className="mz-full-width">
            <h5 className="mz-contact-details-step__title">{languageController.getCopy('global', 'fillContactForm')}</h5>
          </div>
        </div>
      </div>
      <FormAndCalloutBox>
        <FormAndCalloutBox.Form>
          <Form
            type="contact-details"
            onSubmit={handleSubmit}
            isDark={false}
            noValidate={true}
            requiredFieldsHelperText={true}
            errorMessage={formError || undefined}
          >
            <FormGroup md={2}>
              <Input
                type="text"
                name="firstname"
                label={languageController.getFormCopy('label', 'firstname')}
                value={firstname as string}
                isRequired={true}
                errorMessage={errors.firstname}
                onChange={handleChange}
                maxLength={50}
              />
              <Input
                type="text"
                name="lastname"
                label={languageController.getFormCopy('label', 'lastname')}
                value={lastname as string}
                isRequired={true}
                errorMessage={errors.lastname}
                onChange={handleChange}
                maxLength={50}
              />
            </FormGroup>
            <Input
              type="email"
              name="email"
              label={languageController.getFormCopy('label', 'email')}
              value={email as string}
              isRequired={true}
              errorMessage={errors.email}
              onChange={handleChange}
              maxLength={320}
            />
            <FormGroup md={2}>
              <Input
                type="text"
                name="postalcode"
                label={languageController.getFormCopy('label', 'postalcode')}
                value={postalcode as string}
                isRequired={true}
                errorMessage={errors.postalcode}
                onChange={handleChange}
                maxLength={7}
              />
              {/*<Input
                type="tel"
                name="phone"
                label={languageController.getFormCopy('label', 'phone')}
                value={phone}
                errorMessage={errors.phone}
                onChange={(e) => {
                  setData({ ...formData, phone: autoFormatPhoneNumber(e.target.value) });
                }}
                maxLength={17}
              />*/}
            </FormGroup>
            <Textarea
              name="message"
              label={languageController.getFormCopy('label', 'message2500')}
              value={message as string}
              errorMessage={errors.message}
              onChange={handleChange}
              maxLength={2500}
              rows={5}
            />
            <Checkbox
              name="optin"
              checked={(optin === 'on')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData({ ...formData, optin: optin === 'off' ? 'on' : 'off' })}
              label={formDisclaimer ? parse(formDisclaimer) : ''}
            />
            <Button
              type="submit"
              className="mz-btn--primary mz-btn--red"
              label={languageController.getCopy('bookServiceAppointment', 'step5Submit')}
              disabled={!isFormFilled}
            />
          </Form>
        </FormAndCalloutBox.Form>
        <FormAndCalloutBox.Callout>
          <CalloutBoxFull
            vehicleData={vehicleData}
            dealerData={selectedDealerData as any}
            userPosition={userPosition}
            dateAndTimeData={dateAndTimeData ? {
              date: dateAndTimeData.date.toISOString().split('T')[0],
              time: dateAndTimeData.time
            } : undefined}
            isOwnVehicleType={true}
          />
        </FormAndCalloutBox.Callout>
      </FormAndCalloutBox>
    </div>
  );
};

export default ContactDetailsStep;
